import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import type { Zone, Measurement, Farm } from '../../types';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { Home, ChevronRight, Delete, ArrowBack } from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`zone-tabpanel-${index}`}
      aria-labelledby={`zone-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ZoneDetail: React.FC = () => {
  const { farmId, zoneId } = useParams<{ farmId: string; zoneId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [farm, setFarm] = useState<Farm | null>(null);
  const [zone, setZone] = useState<Zone | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', severity: 'success' });
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Load zone and measurements
  useEffect(() => {
    const fetchZoneAndMeasurements = async () => {
      if (!farmId || !zoneId || !user) {
        setError('Invalid farm ID, zone ID, or user not authenticated');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch farm details
        const { data: farmData, error: farmError } = await api.farms.getById(farmId);
        
        if (farmError) {
          console.error('Error fetching farm:', farmError);
          throw new Error('Failed to load farm details');
        }
        
        if (!farmData) {
          throw new Error('Farm not found');
        }
        
        setFarm(farmData);
        
        // Fetch zone details
        const { data: zoneData, error: zoneError } = await api.zones.getById(zoneId);
        
        if (zoneError) {
          console.error('Error fetching zone:', zoneError);
          throw new Error('Failed to load zone details');
        }
        
        if (!zoneData) {
          throw new Error('Zone not found');
        }
        
        setZone(zoneData);
        
        // Fetch measurements for this zone
        const { data: measurementsData, error: measurementsError } = await api.measurements.getAll(zoneId);
        
        if (measurementsError) {
          console.error('Error fetching measurements:', measurementsError);
          throw new Error('Failed to load measurements');
        }
        
        setMeasurements(measurementsData || []);
      } catch (error) {
        console.error('Error loading zone and measurements:', error);
        setError('Failed to load zone details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchZoneAndMeasurements();
  }, [farmId, zoneId, user]);

  const handleDeleteZone = async () => {
    if (!zone) return;
    
    try {
      // Delete the zone
      const { error } = await api.zones.delete(zone.id);
      
      if (error) throw error;
      
      setSnackbar({ 
        open: true, 
        message: 'Zone deleted successfully', 
        severity: 'success' 
      });
      
      // Navigate back to farm zones list
      setTimeout(() => {
        navigate(`/farms/${farmId}`);
      }, 1500);
    } catch (error) {
      console.error('Error deleting zone:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to delete zone', 
        severity: 'error' 
      });
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!farm || !zone) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Zone not found.
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/farms')}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Farms
        </MuiLink>
        <MuiLink
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate(`/farms/${farmId}`)}
        >
          {farm.name}
        </MuiLink>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          {zone.name}
        </Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            sx={{ mr: 1 }} 
            onClick={() => navigate(`/farms/${farmId}`)}
            aria-label="back"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            {zone.name}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setConfirmDelete(true)}
        >
          Delete Zone
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />
      
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="zone tabs">
            <Tab label="Overview" id="zone-tab-0" aria-controls="zone-tabpanel-0" />
            <Tab label="Measurements" id="zone-tab-1" aria-controls="zone-tabpanel-1" />
            <Tab label="Charts" id="zone-tab-2" aria-controls="zone-tabpanel-2" />
          </Tabs>
        </Box>
        
        <TabPanel value={activeTab} index={0}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Zone Information</Typography>
            <Typography><strong>Name:</strong> {zone.name}</Typography>
            <Typography><strong>Farm:</strong> {farm.name}</Typography>
            <Typography><strong>Created:</strong> {new Date(zone.created_at).toLocaleDateString()}</Typography>
            <Typography><strong>Measurements:</strong> {measurements.length}</Typography>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Recent Activity</Typography>
              {measurements.length > 0 ? (
                <Typography>
                  Last measurement: {new Date(measurements[0].created_at).toLocaleString()}
                </Typography>
              ) : (
                <Typography color="text.secondary">
                  No measurements recorded yet.
                </Typography>
              )}
            </Box>
          </Paper>
        </TabPanel>
        
        <TabPanel value={activeTab} index={1}>
          <Paper>
            {measurements.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>EC in Coir Bag</TableCell>
                      <TableCell>Moisture VMC %</TableCell>
                      <TableCell>Coir Bag Temp °C</TableCell>
                      <TableCell>Air Temp °C</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {measurements.map((measurement) => (
                      <TableRow key={measurement.id}>
                        <TableCell>{measurement.date}</TableCell>
                        <TableCell>{measurement.time}</TableCell>
                        <TableCell>{measurement.ec_in_coir_bag}</TableCell>
                        <TableCell>{measurement.moisture_vmc}</TableCell>
                        <TableCell>{measurement.coir_bag_temp}</TableCell>
                        <TableCell>{measurement.air_temp}</TableCell>
                        <TableCell>
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No measurements recorded for this zone yet.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/data-entry')}
                >
                  Add Measurement
                </Button>
              </Box>
            )}
          </Paper>
        </TabPanel>
        
        <TabPanel value={activeTab} index={2}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Charts Coming Soon
            </Typography>
            <Typography color="text.secondary">
              Data visualization features will be available in the next update.
            </Typography>
          </Paper>
        </TabPanel>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{zone.name}"? This will also delete all measurements
            associated with this zone. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleDeleteZone} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ZoneDetail;
